import React, { useState, useEffect, useCallback } from 'react';
import {
    Search,
    Download,
    Calendar,
    Filter,
    RefreshCw,
    ChevronLeft,
    ChevronRight,
    FileText
} from 'lucide-react';
import API_BASE_URL from '../../config/api';

const TdsReportContent = ({ isPanel = false }) => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [summary, setSummary] = useState({ total_tds: 0, total_gross: 0, total_net: 0, count: 0 });
    const [searchTerm, setSearchTerm] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('authToken');
            const url = new URL(`${API_BASE_URL}/admin/tds_report.php`);
            if (fromDate) url.searchParams.append('from', fromDate);
            if (toDate) url.searchParams.append('to', toDate);
            if (searchTerm) url.searchParams.append('search', searchTerm);

            const response = await fetch(url.toString(), {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await response.json();
            if (result.success) {
                setData(result.data || []);
                setSummary(result.summary || { total_tds: 0, total_gross: 0, total_net: 0, count: 0 });
            }
        } catch (error) {
            console.error('Failed to fetch TDS report:', error);
        } finally {
            setLoading(false);
        }
    }, [fromDate, toDate, searchTerm]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(amount);
    };

    const totalPages = Math.ceil(data.length / itemsPerPage);
    const paginatedData = data.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const exportToCSV = () => {
        const headers = ["Distributor ID", "Distributor Name", "Gross Amount", "TDS Deducted", "Net Amount", "Date"];
        const rows = data.map(item => [
            item.distributor_id,
            item.distributor_name,
            item.gross_amount,
            item.tds,
            item.net_payout,
            item.payout_date
        ]);

        let csvContent = "data:text/csv;charset=utf-8,"
            + headers.join(",") + "\n"
            + rows.map(e => e.join(",")).join("\n");

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `TDS_Report_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className={`flex flex-col gap-6 ${isPanel ? 'p-6' : ''}`}>
            {/* Header Mini Section (if internal) */}
            {isPanel && (
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-red-50 text-red-600 rounded-xl">
                            <FileText size={20} />
                        </div>
                        <span className="text-sm font-bold text-gray-400 uppercase tracking-widest">TDS Summary</span>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={exportToCSV} className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 transition-colors">
                            <Download size={18} />
                        </button>
                        <button onClick={fetchData} className={`p-2 hover:bg-gray-100 rounded-lg text-gray-500 transition-colors ${loading ? 'animate-spin' : ''}`}>
                            <RefreshCw size={18} />
                        </button>
                    </div>
                </div>
            )}

            {/* Stat Cards Row */}
            <div className={`grid grid-cols-1 md:grid-cols-3 gap-4`}>
                <div className="bg-white p-5 rounded-3xl border border-slate-100 shadow-sm">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Total TDS</p>
                    <h3 className="text-xl font-black text-slate-900">{formatCurrency(summary.total_tds)}</h3>
                </div>
                <div className="bg-white p-5 rounded-3xl border border-slate-100 shadow-sm">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Gross Payout</p>
                    <h3 className="text-xl font-black text-slate-900">{formatCurrency(summary.total_gross)}</h3>
                </div>
                <div className="bg-white p-5 rounded-3xl border border-slate-100 shadow-sm">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Net Payable</p>
                    <h3 className="text-xl font-black text-slate-900">{formatCurrency(summary.total_net)}</h3>
                </div>
            </div>

            {/* Filters Row */}
            <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="relative">
                        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Distributor..."
                            value={searchTerm}
                            onChange={handleSearch}
                            className="w-full pl-10 pr-4 py-3 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-red-500 transition-all font-bold text-xs"
                        />
                    </div>
                    <div className="relative">
                        <Calendar size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            type="date"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            className="w-full pl-10 pr-4 py-3 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-red-500 transition-all font-bold text-xs"
                        />
                    </div>
                    <div className="relative">
                        <Calendar size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            type="date"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            className="w-full pl-10 pr-4 py-3 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-red-500 transition-all font-bold text-xs"
                        />
                    </div>
                    <button
                        onClick={fetchData}
                        className="py-3 bg-gray-900 text-white rounded-2xl font-black uppercase tracking-widest text-[10px] flex items-center justify-center gap-2"
                    >
                        <Filter size={14} />
                        Filter
                    </button>
                </div>
            </div>

            {/* Table Container */}
            <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden flex-1 overflow-x-auto min-h-[300px]">
                <table className="w-full border-collapse">
                    <thead>
                        <tr className="bg-slate-50/50 border-b border-slate-100">
                            <th className="px-6 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">Distributor Details</th>
                            <th className="px-6 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">Gross</th>
                            <th className="px-6 py-4 text-left text-[9px] font-black uppercase tracking-widest text-red-600">TDS</th>
                            <th className="px-6 py-4 text-left text-[9px] font-black uppercase tracking-widest text-emerald-600">Net</th>
                            <th className="px-6 py-4 text-right text-[9px] font-black text-slate-400 uppercase tracking-widest">Date</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                        {loading ? (
                            <tr><td colSpan="5" className="py-20 text-center text-xs font-black text-slate-300 uppercase tracking-widest">Loading...</td></tr>
                        ) : paginatedData.length === 0 ? (
                            <tr><td colSpan="5" className="py-20 text-center text-xs font-black text-slate-300 uppercase tracking-widest">No Records</td></tr>
                        ) : (
                            paginatedData.map((item) => (
                                <tr key={item.id} className="hover:bg-slate-50/70 transition-colors">
                                    <td className="px-6 py-4">
                                        <div className="flex flex-col">
                                            <span className="font-black text-slate-800 text-xs">{item.distributor_name}</span>
                                            <span className="text-[9px] font-bold text-slate-400 font-mono italic">ID: {item.distributor_id}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-xs font-bold text-slate-500">{formatCurrency(item.gross_amount)}</td>
                                    <td className="px-6 py-4 text-xs font-black text-red-500">-{formatCurrency(item.tds)}</td>
                                    <td className="px-6 py-4 text-xs font-black text-emerald-600">{formatCurrency(item.net_payout)}</td>
                                    <td className="px-6 py-4 text-right">
                                        <p className="text-[10px] font-black text-slate-700">{formatDate(item.payout_date)}</p>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between px-2">
                    <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="p-2 border border-slate-200 rounded-xl disabled:opacity-30"
                    >
                        <ChevronLeft size={16} />
                    </button>
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Page {currentPage} of {totalPages}</span>
                    <button
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="p-2 border border-slate-200 rounded-xl disabled:opacity-30"
                    >
                        <ChevronRight size={16} />
                    </button>
                </div>
            )}
        </div>
    );
};

export default TdsReportContent;
